/**
 * Employee Permissions - Single Source of Truth
 *
 * All employee permission definitions live here. Import from this file
 * in all backend and frontend code to ensure consistency.
 *
 * These permissions control what an employee can do across ALL apps
 * (POS, Portal, etc.) - they are tied to the employee, not the app.
 */

import { ConvexError, v } from "convex/values";

import type { Id } from "./_generated/dataModel";
import type { MutationCtx, QueryCtx } from "./_generated/server";

// =============================================================================
// Permission Keys (The Single Source of Truth)
// =============================================================================

/**
 * All available employee permission keys.
 * Add or remove permissions here - all other types/objects derive from this.
 */
export const PERMISSION_KEYS = [
  "access",
  "viewIntegrations",
  "viewEmployees",
  "addEmployee",
  "editEmployee",
  "viewAllOrders",
  "editAllOrders",
  "addCustomItems",
  "voidKitchenItems",
  "issueRefunds",
  "manageDiscounts",
  "editMenus",
  "editStockStatus",
  "manageCashDrawer",
] as const;

// =============================================================================
// TypeScript Types (Derived from PERMISSION_KEYS)
// =============================================================================

/**
 * Union type of all permission key strings
 */
export type PermissionKey = (typeof PERMISSION_KEYS)[number];

/**
 * Object type with all permissions as boolean properties
 */
export type EmployeePermissions = Record<PermissionKey, boolean>;

// =============================================================================
// Display Names (Human-readable labels for UI)
// =============================================================================

/**
 * Human-readable names for each permission (for UI and error messages)
 */
export const PERMISSION_DISPLAY_NAMES: Record<PermissionKey, string> = {
  access: "App Access",
  viewIntegrations: "View Integrations",
  viewEmployees: "View Employees",
  addEmployee: "Add Employee",
  editEmployee: "Edit Employee",
  viewAllOrders: "View All Orders",
  editAllOrders: "Edit All Orders",
  addCustomItems: "Add Custom Items",
  voidKitchenItems: "Void Kitchen Items",
  issueRefunds: "Issue Refunds",
  manageDiscounts: "Manage Discounts",
  editMenus: "Edit Menus",
  editStockStatus: "Edit Stock Status",
  manageCashDrawer: "Manage Cash Drawer",
};

/**
 * UI-friendly descriptions for each permission (for role edit forms)
 */
export const PERMISSION_DESCRIPTIONS: Record<PermissionKey, string> = {
  access: "Allow access to app functions",
  viewIntegrations: "View hardware and integrations",
  viewEmployees: "View employee accounts",
  addEmployee: "Add new employees",
  editEmployee: "Edit employee details",
  viewAllOrders: "View all orders",
  editAllOrders: "Edit all orders",
  addCustomItems: "Add custom items to orders",
  voidKitchenItems: "Void items sent to kitchen",
  issueRefunds: "Issue refunds on orders",
  manageDiscounts: "Create, edit and apply discounts",
  editMenus: "Edit menus, items, and categories",
  editStockStatus: "Toggle item stock status (86)",
  manageCashDrawer: "Record cash deposits and withdrawals",
};

// =============================================================================
// Default Values
// =============================================================================

/**
 * Default permissions object with all values set to false
 */
export const DEFAULT_PERMISSIONS: EmployeePermissions = {
  access: false,
  viewIntegrations: false,
  viewEmployees: false,
  addEmployee: false,
  editEmployee: false,
  viewAllOrders: false,
  editAllOrders: false,
  addCustomItems: false,
  voidKitchenItems: false,
  issueRefunds: false,
  manageDiscounts: false,
  editMenus: false,
  editStockStatus: false,
  manageCashDrawer: false,
};

// =============================================================================
// Convex Validators
// =============================================================================

/**
 * Convex validator object for permissions (use in schema and function args)
 */
export const permissionsValidator = {
  access: v.boolean(),
  viewIntegrations: v.boolean(),
  viewEmployees: v.boolean(),
  addEmployee: v.boolean(),
  editEmployee: v.boolean(),
  viewAllOrders: v.boolean(),
  editAllOrders: v.boolean(),
  addCustomItems: v.boolean(),
  voidKitchenItems: v.boolean(),
  issueRefunds: v.boolean(),
  manageDiscounts: v.boolean(),
  editMenus: v.boolean(),
  editStockStatus: v.boolean(),
  manageCashDrawer: v.boolean(),
};

// =============================================================================
// Role Templates (Default roles created for new organizations)
// =============================================================================

/**
 * Default role templates with preset permissions.
 * Created for every new organization during auth provisioning.
 */
export const DEFAULT_ROLE_TEMPLATES = {
  manager: {
    name: "Manager",
    type: "default" as const,
    permissions: {
      access: true,
      viewIntegrations: true,
      viewEmployees: true,
      addEmployee: true,
      editEmployee: true,
      viewAllOrders: true,
      editAllOrders: true,
      addCustomItems: true,
      voidKitchenItems: true,
      issueRefunds: true,
      manageDiscounts: true,
      editMenus: true,
      editStockStatus: true,
      manageCashDrawer: true,
    } satisfies EmployeePermissions,
  },
  cashier: {
    name: "Cashier",
    type: "default" as const,
    permissions: {
      access: true,
      viewIntegrations: false,
      viewEmployees: false,
      addEmployee: false,
      editEmployee: false,
      viewAllOrders: true,
      editAllOrders: false,
      addCustomItems: false,
      voidKitchenItems: false,
      issueRefunds: false,
      manageDiscounts: false,
      editMenus: false,
      editStockStatus: false,
      manageCashDrawer: false,
    } satisfies EmployeePermissions,
  },
  server: {
    name: "Server",
    type: "default" as const,
    permissions: {
      access: true,
      viewIntegrations: false,
      viewEmployees: false,
      addEmployee: false,
      editEmployee: false,
      viewAllOrders: true,
      editAllOrders: true,
      addCustomItems: true,
      voidKitchenItems: false,
      issueRefunds: false,
      manageDiscounts: false,
      editMenus: false,
      editStockStatus: true,
      manageCashDrawer: false,
    } satisfies EmployeePermissions,
  },
  host: {
    name: "Host",
    type: "default" as const,
    permissions: {
      access: true,
      viewIntegrations: false,
      viewEmployees: false,
      addEmployee: false,
      editEmployee: false,
      viewAllOrders: true,
      editAllOrders: false,
      addCustomItems: false,
      voidKitchenItems: false,
      issueRefunds: false,
      manageDiscounts: false,
      editMenus: false,
      editStockStatus: false,
      manageCashDrawer: false,
    } satisfies EmployeePermissions,
  },
  kitchen: {
    name: "Kitchen Staff",
    type: "default" as const,
    permissions: {
      access: true,
      viewIntegrations: false,
      viewEmployees: false,
      addEmployee: false,
      editEmployee: false,
      viewAllOrders: true,
      editAllOrders: false,
      addCustomItems: false,
      voidKitchenItems: false,
      issueRefunds: false,
      manageDiscounts: false,
      editMenus: false,
      editStockStatus: true,
      manageCashDrawer: false,
    } satisfies EmployeePermissions,
  },
} as const;

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Create a permissions object by merging multiple permission sets.
 * If ANY role grants a permission, the result is true (union logic).
 */
export function mergePermissions(
  permissionSets: EmployeePermissions[]
): EmployeePermissions {
  const merged = { ...DEFAULT_PERMISSIONS };
  for (const key of PERMISSION_KEYS) {
    merged[key] = permissionSets.some((p) => p[key]);
  }
  return merged;
}

/**
 * Check if a permissions object has a specific permission
 */
export function hasPermission(
  permissions: EmployeePermissions | null | undefined,
  key: PermissionKey
): boolean {
  if (!permissions) {
    return false;
  }
  return permissions[key];
}

// =============================================================================
// Backend Permission Enforcement
// =============================================================================

/**
 * Context type for authenticated mutations/queries
 */
type AuthenticatedCtx = (QueryCtx | MutationCtx) & {
  userId: string;
  organizationDbId: Id<"organizations">;
};

/**
 * Result of looking up a user's employee and permissions.
 * Provides detailed information for proper error messaging.
 */
export interface EmployeePermissionLookupResult {
  /** Whether the user has an employee linked in this organization */
  hasEmployee: boolean;
  /** Whether the linked employee is disabled */
  isDisabled: boolean;
  /** The employee ID (for audit logging, etc.) */
  employeeId: Id<"employees"> | null;
  /** The employee's display name */
  employeeName: string | null;
  /** The aggregated permissions from all roles (null if no roles or disabled) */
  permissions: EmployeePermissions | null;
}

/**
 * Core permission lookup - Single source of truth.
 * Looks up employee by userId and returns their aggregated permissions.
 *
 * This is the BASE function that all other permission functions should use.
 *
 * @param ctx - Database context
 * @param userId - auth user ID
 * @param organizationId - Organization to check permissions in
 */
export async function getEmployeePermissionsByUserId(
  ctx: QueryCtx | MutationCtx,
  userId: string,
  organizationId: Id<"organizations">
): Promise<EmployeePermissionLookupResult> {
  // 1. Look up all employees by userId (user may have employees in multiple orgs)
  const employeesByUser = [];
  for await (const row of ctx.db
    .query("employees")
    .withIndex("by_user_id", (q) => q.eq("userId", userId))) {
    employeesByUser.push(row);
  }

  // Find the employee for the specified organization
  const employee = employeesByUser.find(
    (emp) => emp.organizationId === organizationId
  );

  // No employee found for this user in this organization
  if (!employee) {
    return {
      hasEmployee: false,
      isDisabled: false,
      employeeId: null,
      employeeName: null,
      permissions: null,
    };
  }

  const employeeName = `${employee.firstName} ${employee.lastName}`;

  // Employee is disabled
  if (employee.disabled) {
    return {
      hasEmployee: true,
      isDisabled: true,
      employeeId: employee._id,
      employeeName,
      permissions: null,
    };
  }

  // 2. Get permissions using the shared function
  const permissions = await getPermissionsForEmployee(ctx, employee._id);

  return {
    hasEmployee: true,
    isDisabled: false,
    employeeId: employee._id,
    employeeName,
    permissions, // null if no roles assigned
  };
}

/**
 * Get the current user's employee permissions.
 * Convenience wrapper for authenticated context.
 * Returns null if user has no linked employee record or is disabled.
 */
export async function getEmployeePermissions(
  ctx: AuthenticatedCtx
): Promise<EmployeePermissions | null> {
  const result = await getEmployeePermissionsByUserId(
    ctx,
    ctx.userId,
    ctx.organizationDbId
  );
  return result.permissions;
}

/**
 * Require a specific permission for the current user.
 * Throws ConvexError if user doesn't have the permission.
 *
 * @example
 * async function handler(ctx, args) {
 *   await requirePermission(ctx, "editEmployee");
 *   // ... rest of handler
 * }
 */
export async function requirePermission(
  ctx: AuthenticatedCtx,
  permission: PermissionKey
): Promise<void> {
  const permissions = await getEmployeePermissions(ctx);

  if (!permissions) {
    throw new ConvexError({
      code: "PERMISSION_DENIED",
      message:
        "No employee profile linked to your account. Contact your organization admin.",
    });
  }

  if (!hasPermission(permissions, permission)) {
    throw new ConvexError({
      code: "PERMISSION_DENIED",
      message: `You don't have permission to ${PERMISSION_DESCRIPTIONS[permission].toLowerCase()}.`,
    });
  }
}

/**
 * Get the current user's employee record in their organization.
 * Returns null if user has no linked employee record.
 */
export async function getCurrentEmployee(ctx: AuthenticatedCtx): Promise<{
  _id: Id<"employees">;
  organizationId: Id<"organizations">;
} | null> {
  // Query all employees for this user (may have employees in multiple orgs)
  const employeesByUser = [];
  for await (const row of ctx.db
    .query("employees")
    .withIndex("by_user_id", (q) => q.eq("userId", ctx.userId))) {
    employeesByUser.push(row);
  }

  // Find the employee for the current organization
  const employee = employeesByUser.find(
    (emp) => emp.organizationId === ctx.organizationDbId
  );

  if (!employee || employee.disabled) {
    return null;
  }

  return { _id: employee._id, organizationId: employee.organizationId };
}

/**
 * Check if the current user has permission OR is the owner of the resource.
 * Useful for "edit own or have editAllOrders permission" scenarios.
 *
 * @param ctx - Authenticated context
 * @param permission - Permission to check if not owner
 * @param ownerEmployeeId - The employee ID that owns the resource
 * @returns true if user is owner or has permission, false otherwise
 */
export async function hasPermissionOrIsOwner(
  ctx: AuthenticatedCtx,
  permission: PermissionKey,
  ownerEmployeeId: Id<"employees"> | undefined
): Promise<boolean> {
  const currentEmployee = await getCurrentEmployee(ctx);

  // If current employee is the owner, allow access
  if (
    currentEmployee &&
    ownerEmployeeId &&
    currentEmployee._id === ownerEmployeeId
  ) {
    return true;
  }

  // Otherwise, check if they have the required permission
  const permissions = await getEmployeePermissions(ctx);
  return hasPermission(permissions, permission);
}

/**
 * Require permission OR ownership of a resource.
 * Throws ConvexError if user doesn't have permission and is not the owner.
 *
 * @param ctx - Authenticated context
 * @param permission - Permission required if not owner
 * @param ownerEmployeeId - The employee ID that owns the resource
 */
export async function requirePermissionOrOwnership(
  ctx: AuthenticatedCtx,
  permission: PermissionKey,
  ownerEmployeeId: Id<"employees"> | undefined
): Promise<void> {
  const allowed = await hasPermissionOrIsOwner(
    ctx,
    permission,
    ownerEmployeeId
  );

  if (!allowed) {
    throw new ConvexError({
      code: "PERMISSION_DENIED",
      message: `You don't have permission to ${PERMISSION_DESCRIPTIONS[permission].toLowerCase()}.`,
    });
  }
}

/**
 * Get permissions for a specific employee by their ID.
 * Used when checking permissions for someone other than the current user
 * (e.g., when validating a PIN-based authorization).
 *
 * @param ctx - Database context (Query or Mutation)
 * @param employeeId - The employee ID to get permissions for
 * @returns Merged permissions object or null if employee has no roles
 */
export async function getPermissionsForEmployee(
  ctx: QueryCtx | MutationCtx,
  employeeId: Id<"employees">
): Promise<EmployeePermissions | null> {
  // Get employee roles
  const employeeRoles = [];
  for await (const row of ctx.db
    .query("employeeRoles")
    .withIndex("by_employee_id", (q) => q.eq("employeeId", employeeId))) {
    employeeRoles.push(row);
  }

  if (!employeeRoles.length) {
    return null;
  }

  // Get all organization roles for this employee
  const organizationRoles = await Promise.all(
    employeeRoles.map((er) => ctx.db.get("organizationRoles", er.roleId))
  );
  const validRoles = organizationRoles.filter(
    (r): r is NonNullable<typeof r> => r !== null
  );

  if (!validRoles.length) {
    return null;
  }

  // Get permissions from ALL roles and merge
  const allPermissions = await Promise.all(
    validRoles.map((role) =>
      ctx.db.get("organizationPermissions", role.permissionId)
    )
  );
  const validPermissions = allPermissions.filter(
    (p): p is NonNullable<typeof p> => p !== null
  );

  if (!validPermissions.length) {
    return null;
  }

  // Merge permissions: if ANY role has permission = true, grant it
  return mergePermissions(validPermissions);
}
